import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import { FormCard } from '../../..';
import Spinner from '../../../CargillLeafSpinner';
import { ErrorIcon, ErrorValidation } from '../../../ErrorValidation';
import type { CardFieldProps, CardStore } from '../../types';

const category = CARD_CATEGORIES.privacyPolicy;

const textSx = {
  color: 'var(--sprout-text-primary)',
  font: 'var(--sprout-font-body1)'
};

const errorValidationSx = {
  ...textSx,
  display: 'flex !important',
  justifyContent: 'flex-start !important',
  alignItems: 'flex-start !important',
  textAlign: 'left !important',
  font: 'var(--sprout-font-body1)',
  color: 'var(--sprout-text-primary) !important'
};

interface PrivacyPolicyCardProps {
  isReview?: boolean;
  disableAllFields?: boolean;
  store: CardStore;
  [key: string]: unknown;
}

function CheckBox({ t, apiService, dispatch, store }: CardFieldProps) {
  const [checked, setChecked] = useState(false);
  const [progress, setProgress] = useState(false);
  const { customer: { id: requestId = '' } = {} } = store;
  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const updatedState = event.target.checked;
    setProgress(true);
    setChecked(updatedState);
    if (updatedState) {
      await apiService.savePolicyResponse(requestId, dispatch);
    }
    setProgress(false);
  };

  return (
    <FormGroup
      sx={{
        mb: '16px',
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...textSx,
        '& .MuiFormControlLabel-label': {
          color: 'var(--sprout-text-primary) !important',
          font: 'var(--sprout-font-body1) !important'
        }
      }}
    >
      <FormControlLabel
        sx={{
          m: 0,
          gap: '16px',
          alignItems: 'flex-start',
          '& .MuiCheckbox-root': {
            p: 0
          },
          '& .MuiFormControlLabel-label': {
            marginTop: 0
          }
        }}
        control={
          progress ? (
            <Box sx={{ ml: 1, mr: 1, display: 'flex', alignItems: 'center' }}>
              <Spinner size={24} />
            </Box>
          ) : (
            <Checkbox
              checked={checked}
              sx={{ p: 0, mt: 0 }}
              // TEMP: always enabled to verify clicking behavior.
              disabled={false}
              onChange={handleChange}
              slotProps={{
                input: {
                  'aria-label': t('financial.privacyPolicy.PrivacyPolicyCheckBox.label')
                }
              }}
            />
          )
        }
        label={t('financial.privacyPolicy.PrivacyPolicyCheckBox.label')}
      />

      <Link
        sx={{ ml: 1 }}
        data-event="common-click"
        data-metrics-desc="privacy"
        data-metrics-type="BUTTON"
        rel="noopener noreferrer"
        href="https://www.cargill.com/page/privacy"
        target="_blank"
        underline="always"
      >
        {t('financial.privacyPolicy.PrivacyPolicyCheckBox.linkText')}
      </Link>
    </FormGroup>
  );
}

function PrivacyPolicyCard(props: PrivacyPolicyCardProps) {
  const { isReview, disableAllFields, store } = props;
  const { t } = useTranslation();
  const { common: { policyAccepted = false } = {} } = store;
  const cardProps = {
    cardId: category,
    title: t('financial.privacyPolicy.title'),
    subtitle: t('financial.privacyPolicy.subtitle'),
    image: '/food/onboarding/assets/Privacy_Policy.svg',
    isReview
  };

  if (policyAccepted === true) return null;
  return (
    <FormCard hideCard={policyAccepted === true} disableCardStyles={policyAccepted === true} {...cardProps}>
      {isReview && policyAccepted !== true && (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ErrorValidation is a styled.div with no `sx` prop in its published types; harmless styling passthrough
        <ErrorValidation {...({ sx: errorValidationSx } as any)}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              textAlign: 'left',
              width: '100%',
              gap: 1,
              pt: '16px',
              pb: '16px'
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: '1px' }}>
              <ErrorIcon />
            </Box>
            <Typography
              component="span"
              sx={{
                color: 'var(--sprout-text-primary)!important',
                font: 'var(--sprout-font-body1)!important',
                lineHeight: '24px',
                textAlign: 'left'
              }}
            >
              {t('financial.privacyPolicy.PrivacyPolicyCheckBox.policyError')}
            </Typography>
          </Box>
        </ErrorValidation>
      )}
      <CheckBox disabled={isReview || disableAllFields} t={t} {...props} />
    </FormCard>
  );
}

export default PrivacyPolicyCard;
